import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, UserPlus, Trash2, Copy, Check, Users, Mail, Calendar,
  Eye, EyeOff, Pencil
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { API, getAuthHeaders } from '../App';

export default function AdminContentCreators() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCreator, setEditingCreator] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  
  const [editForm, setEditForm] = useState({
    full_name: '',
    password: '',
    is_active: true
  });

  const fetchCreators = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/media/content-creators`, {
        headers: getAuthHeaders()
      });
      setCreators(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCreators();
  }, [fetchCreators]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!form.email || !form.password || !form.full_name) {
      toast.error('Заполните все поля');
      return;
    }
    
    try {
      await axios.post(`${API}/media/content-creators`, form, {
        headers: getAuthHeaders()
      });
      
      toast.success('Контент-мейкер создан');
      setShowAddDialog(false);
      setForm({ email: '', password: '', full_name: '' });
      fetchCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/media/content-creators/${deleteId}`, {
        headers: getAuthHeaders()
      });
      
      toast.success('Контент-мейкер удалён');
      setDeleteId(null);
      fetchCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const openEditDialog = (creator) => {
    setEditingCreator(creator);
    setEditForm({
      full_name: creator.full_name || '',
      password: '',
      is_active: creator.is_active !== false
    });
    setShowEditDialog(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingCreator) return;
    
    try {
      const updateData = {
        full_name: editForm.full_name
      };
      
      // Only include password if it was changed
      if (editForm.password) {
        updateData.password = editForm.password;
      }
      
      updateData.is_active = editForm.is_active;
      
      await axios.put(`${API}/media/content-creators/${editingCreator.id}`, updateData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Контент-мейкер обновлён');
      setShowEditDialog(false);
      setEditingCreator(null);
      fetchCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm({ ...form, password });
  };

  const generateEditPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setEditForm({ ...editForm, password });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-emerald-600" />
                <div>
                  <h1 className="text-xl font-bold">Контент-мейкеры</h1>
                  <p className="text-sm text-gray-500">Управление доступом к Media Studio</p>
                </div>
              </div>
            </div>
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Добавить
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Info Card */}
        <Card className="mb-6 bg-emerald-50 border-emerald-200">
          <CardContent className="pt-4">
            <p className="text-sm text-emerald-800">
              <strong>Media Studio URL:</strong>{' '}
              <code className="bg-emerald-100 px-2 py-1 rounded">
                {window.location.origin}/{currentLang}/media-studio
              </code>
              <button
                onClick={() => copyToClipboard(`${window.location.origin}/${currentLang}/media-studio`, 'url')}
                className="ml-2 text-emerald-600 hover:text-emerald-700"
              >
                {copiedId === 'url' ? <Check className="w-4 h-4 inline" /> : <Copy className="w-4 h-4 inline" />}
              </button>
            </p>
            <p className="text-sm text-emerald-700 mt-2">
              Создайте аккаунт и отправьте данные контент-мейкеру. Он сможет загружать фото в папки.
            </p>
          </CardContent>
        </Card>

        {/* Creators List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : creators.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Нет контент-мейкеров</h3>
              <p className="text-gray-500 mb-4">Добавьте первого контент-мейкера</p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Добавить
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {creators.map((creator) => (
              <Card key={creator.id}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-emerald-600 font-bold text-lg">
                          {creator.full_name?.charAt(0)?.toUpperCase() || 'C'}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium">{creator.full_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Mail className="w-4 h-4" />
                          {creator.email}
                          <button
                            onClick={() => copyToClipboard(creator.email, creator.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {copiedId === creator.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                          <Calendar className="w-3 h-3" />
                          Создан: {new Date(creator.created_at).toLocaleDateString('ru')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(creator)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteId(creator.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новый контент-мейкер</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Имя</Label>
              <Input
                placeholder="Имя контент-мейкера"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Пароль</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Пароль"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button type="button" variant="outline" onClick={generatePassword}>
                  Сгенерировать
                </Button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Отмена
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Создать
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактировать контент-мейкера</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label>Имя</Label>
              <Input
                placeholder="Имя контент-мейкера"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Новый пароль (оставьте пустым, чтобы не менять)</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Новый пароль"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button type="button" variant="outline" onClick={generateEditPassword}>
                  Сгенерировать
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={editForm.is_active}
                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                className="w-4 h-4 text-emerald-600 rounded border-gray-300"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Активен</Label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Отмена
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Сохранить
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить контент-мейкера?</AlertDialogTitle>
            <AlertDialogDescription>
              Аккаунт будет удалён. Загруженные файлы останутся в галерее.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
